import { ICountry, SellerFormValueType } from '@interfaces/models/seller-info/seller-information';
import { dateIsValid } from '@helpers/utils/date-utils';

export type ValidationRules = Record<string, ((value: string) => string | undefined)[]>;

const CHARACTER_REGEX_PATTERN = /^(?!^(\w)\1*$)[\wÀ-ÿ\s-+]*$/;

const required = (value: string): string => {
  return !value ? 'GET_PAID.PERSONAL_INFORMATION_FORM.ERRORS.FIELD_REQUIRED' : undefined;
};

const pattern = (value: string, regex?: RegExp): string | undefined => {
  if (!regex) {
    return;
  }
  return value && !regex.test(value) ? 'GET_PAID.TAX_INFORMATION_FORM.ERROR.INVALID_TIN' : undefined;
};

export const validateField = (fieldName, value, fieldValidations) => {
  for (const validator of fieldValidations[fieldName]) {
    const error = validator(value);
    if (error) {
      return error;
    }
  }
  return null;
};

export const validateForm = (values, fieldValidations) => {
  const errors = {} as SellerFormValueType;
  for (const fieldName in fieldValidations) {
    if (fieldValidations.hasOwnProperty(fieldName)) {
      const error = validateField(fieldName, values[fieldName], fieldValidations);
      if (error) {
        errors[fieldName] = error;
      }
    }
  }
  return errors;
};

export const flattenObject = (obj, parentKey = '', separator = '_') => {
  const flattened = {};

  for (const key in obj) {
    if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
      // prevent state key to overwrite the country iso2 code
      if (key === 'state') {
        obj[key] = { state: obj[key]?.iso2, statename: obj[key]?.name };
      }
      if (key === 'businessInformation') {
        obj[key] = { businessName: obj[key]?.name };
      }
      const nested = flattenObject(obj[key], parentKey + key + separator, separator);
      Object.assign(flattened, nested);
    } else {
      // Remove the parent key prefix and add the key without it
      const flattenedKey = parentKey ? key.replace(parentKey, '') : key;
      flattened[flattenedKey] = obj[key];
    }
  }

  return flattened;
};

export const generateValidationRules = (userFlags): ValidationRules => {
  return {
    firstname: userFlags?.isUSC2CUser || userFlags?.isEUC2CUser ? [required, (value) => pattern(value)] : [],
    lastname: userFlags?.isUSC2CUser || userFlags?.isEUC2CUser ? [required, (value) => pattern(value)] : [],
    tin: [required, (value) => pattern(value, CHARACTER_REGEX_PATTERN)],
    country: [required],
    countries: [],
    state: [], // state is dependent on a country
    day: userFlags?.isEUC2CUser || userFlags?.isUSC2CUser ? [required] : [],
    month: userFlags?.isEUC2CUser || userFlags?.isUSC2CUser ? [required] : [],
    year: userFlags?.isEUC2CUser || userFlags?.isUSC2CUser ? [required] : [],
    vat: userFlags?.isEUB2CUser ? [(value) => pattern(value, CHARACTER_REGEX_PATTERN)] : [],
    businessName: userFlags?.isUSB2CUser || userFlags?.isEUB2CUser ? [required, (value) => pattern(value)] : [],
    street: [required, (value) => pattern(value)],
    city: [required, (value) => pattern(value)],
    zip: [required, (value) => pattern(value)],
    isElectronically: userFlags?.isUSUser ? [required, (value) => pattern(value)] : [],
  };
};

export const validateDateFields = (values: SellerFormValueType, userIsMajor) => {
  const errors = {
    dateError: false,
    ageError: false,
  };

  if (values.day && values.month && values.year) {
    const isDateValid = dateIsValid(Number(values.day), Number(values.month), Number(values.year));
    const isUserMajor = userIsMajor(`${values.year}-${values.month}-${values.day}`);

    errors.dateError = !isDateValid;
    errors.ageError = !isUserMajor;
  }

  return errors;
};

export const getSelectedCountry = (countries: ICountry[], countryCode: string): ICountry => {
  return countries.find((c) => c.code.toLowerCase() === countryCode?.toLowerCase());
};

export const isStateFieldRequired = (countryCode, stateFieldRequiredCountries) => {
  return stateFieldRequiredCountries?.includes(countryCode?.toUpperCase());
};

export const validateState = (values, stateFieldRequired, t) => {
  if (stateFieldRequired) {
    const stateError = required(values?.state);
    if (stateError) {
      return { state: t(stateError) };
    }
  }
  return {};
};
